import {createContext, useCallback, useEffect} from "react";
import {useLocation} from "react-router-dom";

import {useDevice} from "@/hooks/useDevice/useDevice.js";
import {config} from "@/config";

export const TrackingContext = createContext("");

const gtmId = "GTM-KSMM9NLX";
const isProduction = import.meta.env.PROD;

globalThis.window.dataLayer = globalThis.window.dataLayer || [];
const {websiteId} = config;
const isForth = websiteId === 1;
const isEdge = websiteId === 2;
const isBiomarkerTracking = !isForth && !isEdge;

export const TrackingProvider = ({children}) => {
  const {pathname} = useLocation();
  const {isDesktop, isIOS, isAndroid} = useDevice();
  const gtmBanList = ["bespoke-test", "edit-test"];

  const setGoogleTagManagerScripts = useCallback((googleId = "") => {
    if (!isProduction) return;
    const gtmScripts = document.querySelectorAll("script[src^='https://www.googletagmanager.com']") || [];
    if (gtmScripts.length > 0) return;

    const id = googleId || gtmId;

    const script = document.createElement("script");
    script.id = "gtm-script";
    script.innerText = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${id}');`;

    document.head.prepend(script);

    document.body.insertAdjacentHTML(
      "beforeend",
      `
      <!-- Google Tag Manager (noscript) -->
      <noscript id='gtm-iframe'><iframe src="https://www.googletagmanager.com/ns.html?id=${id}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
      <!-- End Google Tag Manager (noscript) -->
    `,
    );
  }, []);

  const deleteGoogleTagManagerScripts = useCallback((googleId = "") => {
    if (!isProduction) return;

    if (googleId) {
      const scripts = document.querySelectorAll(`script[src*='${googleId}']`) || [];
      scripts?.forEach(script => script.remove());
      return;
    }

    const scripts = document.querySelectorAll("script[src^='https://www.googletagmanager.com']") || [];
    scripts?.forEach(script => script.remove());
  }, []);

  const handleAppViewTracking = useCallback(() => {
    if (!isProduction) return;
    let device = "";

    if (isForth) {
      if (isDesktop) device = "Forth_Browser";
      if (isIOS) device = "Forth_iOS";
      if (isAndroid) device = "Forth_Android";
    }

    if (isEdge) {
      if (isDesktop) device = "Edge_Browser";
      if (isIOS) device = "Edge_iOS";
      if (isAndroid) device = "Edge_Android";
    }

    if (isBiomarkerTracking) {
      if (isDesktop) device = "BT_Browser";
      if (isIOS) device = "BT_iOS";
      if (isAndroid) device = "BT_Android";
    }

    if (device) {
      globalThis.window.dataLayer.push({
        appView: device,
      });
    }
  }, [isDesktop, isIOS, isAndroid]);

  const handleClarityTracking = () => {
    if (!isProduction) return;

    const hasClarityScripts = document.querySelectorAll("script#clarity-script").length > 0;
    if (hasClarityScripts) return;

    const c = document.createElement("script");
    c.innerHTML = `(function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "enipenx71w");`;
    c.id = "clarity-script";
    c.defer = true;

    document.head.appendChild(c);
  };

  useEffect(() => {
    if (!gtmBanList.includes(pathname.split("/")[1])) {
      setGoogleTagManagerScripts();
    } else {
      deleteGoogleTagManagerScripts();
    }

    if (["bespoke-test", "edit-test"].includes(pathname.split("/")[1])) {
      handleClarityTracking();
    }
  }, [pathname]);

  useEffect(() => {
    if (!gtmBanList.includes(pathname.split("/")[1])) {
      handleAppViewTracking();
    }
  }, [pathname, isDesktop, isAndroid, isIOS]);

  return (
    <TrackingContext.Provider
      value={{gtmId, setGoogleTagManagerScripts, deleteGoogleTagManagerScripts, handleAppViewTracking}}>
      {children}
    </TrackingContext.Provider>
  );
};
